import type { GridRenderCellParams } from '@mui/x-data-grid';
import type { AdSetEntry } from '../api/getAdSets';
import { useFormContext } from 'react-hook-form';
import type { MouseEvent } from 'react';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { isDuplicatesCounts } from '../types';
import { TextField } from 'components/common';
import { Box, Button, styled } from '@mui/material';
import { CopyAll } from '@mui/icons-material';
import { useInView } from 'react-intersection-observer';

interface MemoizedAdSetDuplicatesCountFieldProps {
  cellParams: GridRenderCellParams<AdSetEntry>;
}

interface ComputedValues {
  currentRow: AdSetEntry | undefined;
  isSelected: boolean;
  initialDuplicatesCount: string;
  dynamicFieldName: string;
}

const Container = styled(Box)({
  position: 'relative',
  height: '100%',
  gap: '8px',
  display: 'flex',
  flexWrap: 'nowrap',
  justifyContent: 'flex-start',
  alignItems: 'center',
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    paddingRight: theme.spacing(0.75),
    minWidth: 80,
  },
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
    display: 'none',
  },
  '& input[type=number]': {
    MozAppearance: 'textfield',
  },
}));

const ApplyButton = styled(Button)(({ theme }) => ({
  textTransform: 'initial',
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
}));

const INPUT_PROPS = {
  type: 'number' as const,
  inputProps: {
    min: 1,
    max: 5,
    'aria-label': 'Duplicates count',
  },
} as const;

const stopPropagation = (e: MouseEvent) => {
  e.stopPropagation();
};

const findSelectedRow = (rows: AdSetEntry[], id: string): AdSetEntry | undefined =>
  rows?.find(row => row.id === id);

const getDuplicatesCount = (row: AdSetEntry | undefined): number | undefined =>
  isDuplicatesCounts(row) ? row.duplicates_count : 1;

const createFieldName = (id: string): string => `selectedAdSetsDuplicatesCount.${id}`;

const useComputedValues = (selectedAdSetsRows: AdSetEntry[], currentId: string): ComputedValues => {
  const currentRow = useMemo(
    () => findSelectedRow(selectedAdSetsRows, currentId),
    [selectedAdSetsRows, currentId],
  );

  const isSelected = useMemo(
    () => !!currentRow,
    [currentRow],
  );

  const initialDuplicatesCount = useMemo(
    () => getDuplicatesCount(currentRow),
    [currentRow],
  );

  const dynamicFieldName = useMemo(
    () => createFieldName(currentId),
    [currentId],
  );

  return {
    currentRow,
    isSelected,
    initialDuplicatesCount: initialDuplicatesCount ? String(initialDuplicatesCount) : '1',
    dynamicFieldName,
  };
};

const useApplyToAll = (dynamicFieldName: string) => {
  const { getValues, setValue } = useFormContext();

  return useCallback(() => {
    try {
      const duplicatesCountToAll = getValues(dynamicFieldName);
      const selectedAdSetsDuplicatesCount: Record<string, string> = getValues('selectedAdSetsDuplicatesCount');

      const updatedValues = Object.fromEntries(
        Object.keys(selectedAdSetsDuplicatesCount).map(id => [id, duplicatesCountToAll]),
      );

      setValue('selectedAdSetsDuplicatesCount', updatedValues, {
        shouldValidate: true,
      });

    } catch (error) {
      console.error('Error applying duplicates count to all ad sets:', error);
    }
  }, [dynamicFieldName, getValues, setValue]);
};

const useFieldInitialization = (
  isSelected: boolean,
  dynamicFieldName: string,
  initialDuplicatesCount: string,
) => {
  const { getValues, setValue } = useFormContext();

  useEffect(() => {
    if (!isSelected) return;

    const currentValue = getValues(dynamicFieldName);

    if (!currentValue) {
      setValue(dynamicFieldName, initialDuplicatesCount, {
        shouldValidate: true,
      });
    }
  }, [isSelected, dynamicFieldName, initialDuplicatesCount, getValues, setValue]);
};

const MemoizedTextFieldComponent = memo<{
  name: string;
  type: string;
  InputProps: typeof INPUT_PROPS;
  errorAsTooltip: boolean;
}>(({ name, type, InputProps, errorAsTooltip }) => (
  <StyledTextField
    name={name}
    type={type}
    InputProps={InputProps}
    errorAsTooltip={errorAsTooltip}
  />
));

MemoizedTextFieldComponent.displayName = 'MemoizedTextFieldComponent';

const MemoizedApplyButtonComponent = memo<{
  onClick: () => void;
}>(({ onClick }) => (
  <ApplyButton
    data-testid={'applyToAllDuplicatesCountButton'}
    onClick={onClick}
    size="small"
    startIcon={<CopyAll />}
    aria-label="Apply duplicates count to all selected ad sets"
    variant="text"
    color="primary"
  >
    Apply to all
  </ApplyButton>
));

MemoizedApplyButtonComponent.displayName = 'MemoizedApplyButtonComponent';

const AdSetDuplicatesCountFieldComponent = ({ cellParams }: MemoizedAdSetDuplicatesCountFieldProps) => {
  const currentId = cellParams.row.id;
  const { watch } = useFormContext();

  const selectedAdSetsRows: AdSetEntry[] = watch('selectedAdSets');

  const { isSelected, initialDuplicatesCount, dynamicFieldName } = useComputedValues(
    selectedAdSetsRows,
    currentId,
  );

  useFieldInitialization(isSelected, dynamicFieldName, initialDuplicatesCount);

  const handleApplyToAll = useApplyToAll(dynamicFieldName);

  const { ref, inView } = useInView({
    threshold: 0,
  });

  if (!isSelected) {
    return null;
  }

  return (
    <Container onClick={stopPropagation} ref={ref}>
      {
        inView && <>
          <MemoizedTextFieldComponent
            name={dynamicFieldName}
            type="number"
            InputProps={INPUT_PROPS}
            errorAsTooltip
          />

          {(selectedAdSetsRows?.length ?? 0) > 1 && (
            <Box className="MuiInputAdornment-positionEnd">
              <MemoizedApplyButtonComponent onClick={handleApplyToAll} />
            </Box>
          )}
        </>
      }
    </Container>
  );
};

const arePropsEqual = (
  prevProps: MemoizedAdSetDuplicatesCountFieldProps,
  nextProps: MemoizedAdSetDuplicatesCountFieldProps,
): boolean => {
  const prevRow = prevProps.cellParams.row;
  const nextRow = nextProps.cellParams.row;

  return (
    prevRow.id === nextRow.id &&
    prevRow.duplicates_count === nextRow.duplicates_count
  );
};

export const MemoizedAdSetDuplicatesCountField = memo(
  AdSetDuplicatesCountFieldComponent,
  arePropsEqual,
);

MemoizedAdSetDuplicatesCountField.displayName = 'MemoizedAdSetDuplicatesCountField';

export type { MemoizedAdSetDuplicatesCountFieldProps };